import { useState } from "react";
import { ArrowSquareOut, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ResearchButton } from "@/components/Research";

export const TOPIC_LABELS = {
  all: "Everything",
  manufacturing: "Manufacturing",
  steel_metals: "Steel & metals",
  freight_logistics: "Freight & logistics",
  trade_tariffs: "Trade & tariffs",
  safety: "Workplace safety",
};

export const TopicFilter = ({ value, onChange, testid = "news-topics" }) => (
  <div data-testid={testid} className="mt-6 flex snap-x gap-2 scroll-x overflow-x-auto pb-1">
    {Object.entries(TOPIC_LABELS).map(([key, label]) => (
      <button
        key={key}
        data-testid={`news-topic-${key}`}
        onClick={() => onChange(key)}
        className={`whitespace-nowrap border px-3 py-2 text-xs font-semibold transition-colors duration-150 ${
          value === key
            ? "border-[#002FA7] bg-[#002FA7] text-white"
            : "border-border bg-white text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const when = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export const HeadlineCard = ({ item, index, canResearch = false }) => {
  const [line, setLine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const explain = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const { data } = await api.post("/news/why-it-matters", { title: item.title, source: item.source });
      setLine(data.line);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-testid={`news-headline-${index}`} className="border border-border bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[#002FA7]">
        <span>{item.topic_label}</span>
        {item.source && <span className="text-slate-400">· {item.source}</span>}
        {when(item.published) && <span className="text-slate-400">· {when(item.published)}</span>}
      </div>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`news-headline-link-${index}`}
        className="tap-safe mt-2 block text-base font-semibold leading-snug text-[#0F172A] hover:text-[#002FA7]"
      >
        {item.title} <ArrowSquareOut size={14} className="inline align-baseline" />
      </a>

      {line ? (
        <p data-testid={`news-why-line-${index}`} className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2 text-sm text-[#0F172A]">
          {line}
        </p>
      ) : (
        <button
          data-testid={`news-why-btn-${index}`}
          onClick={explain}
          disabled={busy}
          className="mt-3 inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs font-semibold text-slate-600 transition-colors duration-150 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-60"
        >
          <Sparkle size={14} /> {busy ? "Thinking…" : "Why this matters to me"}
        </button>
      )}
      {canResearch && (
        <div className="mt-3">
          <ResearchButton
            label="Background, with sources"
            endpoint="/research/headline"
            body={{ title: item.title, source: item.source }}
            testid={`news-background-btn-${index}`}
          />
        </div>
      )}
      {failed && (
        <p data-testid={`news-why-error-${index}`} className="mt-2 text-xs text-[#B3241B]">
          Could not work that out just now — try again shortly.
        </p>
      )}
    </article>
  );
};

export const AnnouncementCard = ({ post, index, onDelete, canDelete, children }) => (
  <article data-testid={`news-post-${index}`} className="border-l-2 border-[#B3241B] bg-white p-4 sm:p-5">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#B3241B]">
        Our announcement · {post.published_on}
      </p>
      {canDelete && (
        <button
          data-testid={`news-post-delete-${index}`}
          onClick={() => onDelete(post.id)}
          className="text-xs font-semibold text-slate-500 hover:text-[#B3241B]"
        >
          Remove
        </button>
      )}
    </div>
    <h3 className="mt-2 text-base font-semibold text-[#0F172A]">{post.title}</h3>
    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">{post.body}</p>
    {post.link && (
      <a href={post.link} target="_blank" rel="noopener noreferrer" className="tap-safe mt-2 inline-block text-xs font-semibold text-[#002FA7]">
        Read more
      </a>
    )}
    <p className="mt-3 text-xs text-slate-400">{post.author_name}</p>
    {children}
  </article>
);
